import { Box, theme } from '@zappyrent/hundop';
import { isObject, upperFirst } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Media from 'react-media';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import shallow from 'zustand/shallow';

import Error from '../../components/Error';
import detailsAPI from '../../services/api/details';
import { areUserIdentityDocumentsUploaded } from '../../shared/helpers';
import { useUser } from '../../shared/hooks';
import useBookingData from '../../shared/hooks/useBookingData';
import useUserDocuments from '../../shared/hooks/useUserDocuments';
import { IDetailsOwner } from '../../shared/ts/details';
import useStore from '../../store';
import BookingReceipt from './BookingReceipt';
import {
  StyledContinueButton,
  StyledErrorMessage,
  StyledGrid,
  StyledHeading,
  StyledImageContainer,
  StyledInfo,
  StyledList,
  StyledPriceContainer,
  StyledPriceSubContainer,
  StyledSection,
  StyledSectionContainer,
  StyledSectionSubContainer,
  StyledSemiBoldSubtitle,
  StyledShowButton,
  StyledStickyBox,
  StyledSubContainer,
  StyledSubSection,
  StyledSubtitle,
  StyledSubtitleGreen,
  StyledSummaryContainer,
  StyledTextBody,
} from './ThankYou.styles';
import ThankYouAccordion from './ThankYouAccordion';

const ThankYou = (): JSX.Element | null => {
  const { t } = useTranslation(['thankyou', 'common', 'routes', 'errors']);

  const navigate = useNavigate();
  const { locale } = useParams<{ locale: string }>();
  const [searchParams] = useSearchParams();

  const bookingId = searchParams.get('booking_id') ?? searchParams.get('id');
  const bookingData = useBookingData(bookingId);

  const [propertyImage, setPropertyImage] = useState<string | null>(null);
  const [propertyOwner, setPropertyOwner] = useState<IDetailsOwner>({});

  const yourBookingRef = useRef<HTMLDivElement | null>(null);

  const { user, userDocuments } = useStore(
    state => ({
      user: state.user,
      userDocuments: state.userDocuments,
    }),
    shallow,
  );

  const isLoadingUser = useUser();
  const isLoadingUserDocuments = useUserDocuments(user?.id ?? null);

  const clickHandler = () => {
    if (yourBookingRef && yourBookingRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: yourBookingRef.current.offsetTop,
      });
    }
  };

  const continueButton = (
    <StyledContinueButton
      onClick={() => {
        navigate(`/${locale}/${t('navigation:bookings')}?id=${bookingId}`);
      }}
      title={t('common:continue')}
      variant="primary"
    >
      {t('common:continue')}
    </StyledContinueButton>
  );

  useEffect(() => {
    if (bookingData) {
      const { property_id: id } = bookingData;
      const getPropertyDetail = async (id: string) => {
        try {
          const { images, owner } = await detailsAPI.getPropertyDetailsById(id);

          if (images[0] && images[0].url) {
            setPropertyImage(
              `${process.env.REACT_APP_SITE_URL}/${images[0].url}`,
            );
          }
          if (owner) {
            setPropertyOwner(owner);
          }
        } catch (err) {
          console.error(err);
        }
      };

      if (id) getPropertyDetail(id);
    }
  }, [bookingData]);

  if (bookingData !== null && !bookingData.id)
    return (
      <Error errorMessage={upperFirst(t('errors:errorBookingPermission'))} />
    );

  if (bookingData && isObject(bookingData)) {
    const {
      id,
      payment_type,
      card,
      currency,
      end_date,
      fixed_costs,
      monthly_costs,
      start_date,
      tenant,
      tenant_age,
      tenant_comment,
      tenant_gender,
      tenant_proposal,
      tenant_reason,
      prop,
    } = bookingData;

    const monthlyTotal = monthly_costs
      ? monthly_costs + (fixed_costs ?? 0)
      : null;

    return (
      <>
        {!isLoadingUser && !isLoadingUserDocuments && (
          <StyledGrid container>
            <StyledSection id="success-header">
              <Box sm={7} md={4.5} lg={4} xl={3}>
                <StyledHeading tag="h1" sm="32px" md="32px" lg="32px">
                  <span>{t('bookingSent')}</span>
                </StyledHeading>
              </Box>
              <StyledShowButton onClick={clickHandler}>
                {t('showBooking')}
              </StyledShowButton>
            </StyledSection>
            <StyledSectionContainer>
              <StyledSectionSubContainer>
                <StyledSection id="next-steps">
                  <StyledSubtitle tag="strong">{t('nextSteps')}</StyledSubtitle>
                  <StyledSubSection>
                    <StyledSubtitleGreen
                      completed={areUserIdentityDocumentsUploaded(
                        userDocuments,
                      )}
                    >
                      {upperFirst(t('common:verifyIdentity'))}
                    </StyledSubtitleGreen>
                    {!areUserIdentityDocumentsUploaded(userDocuments) && (
                      <>
                        <StyledTextBody>
                          {upperFirst(t('common:uploadFollowingDocuments'))}
                        </StyledTextBody>
                        <StyledList>
                          <li>{upperFirst(t('common:identityDocument'))}</li>
                          <StyledTextBody>
                            {upperFirst(t('common:idCardOrPassport'))}
                          </StyledTextBody>
                          <li>{upperFirst(t('common:fiscalCode'))}</li>
                        </StyledList>
                      </>
                    )}
                  </StyledSubSection>
                  <StyledSubSection>
                    <StyledSubtitleGreen>
                      {upperFirst(t('common:reachTheMeritShare'))}
                    </StyledSubtitleGreen>
                    <StyledList>
                      <li>{upperFirst(t('common:addOneOrMoreGuarantors'))}</li>
                      <StyledTextBody>
                        {upperFirst(t('common:helpAchievingGuarantees'))}
                      </StyledTextBody>
                      <li>{upperFirst(t('common:uploadIncomeDocuments'))}</li>
                      <StyledTextBody>
                        {upperFirst(t('common:forEachAddedGuarantor'))}
                      </StyledTextBody>
                    </StyledList>
                    <StyledInfo>
                      {upperFirst(
                        t('youWillBeAbleToRetrieveAnyGuarantorAlreadyAdded'),
                      )}
                    </StyledInfo>
                  </StyledSubSection>
                </StyledSection>
                <Media
                  queries={{
                    mobile: `(max-width: calc(${theme.breakpoints.m} - 1px))`,
                    desktop: `(min-width: ${theme.breakpoints.m})`,
                  }}
                  defaultMatches={{ desktop: true }}
                >
                  {matches => matches.desktop && continueButton}
                </Media>
              </StyledSectionSubContainer>
              <StyledSection id="your-booking" ref={yourBookingRef}>
                <StyledSubtitle booking tag="strong">
                  {t('yourBooking')}
                </StyledSubtitle>
                <StyledSummaryContainer>
                  <StyledPriceContainer>
                    {propertyImage && (
                      <StyledImageContainer src={propertyImage} />
                    )}
                    <StyledPriceSubContainer>
                      <StyledSubContainer>
                        <StyledSemiBoldSubtitle tag="strong">
                          {tenant_proposal === monthly_costs
                            ? t('rentAmountText')
                            : t('rentAmountTextAlternate')}
                        </StyledSemiBoldSubtitle>
                        {tenant_proposal && (
                          <StyledSemiBoldSubtitle tag="strong">
                            {currency === 'EUR' ? `€ ` : currency}
                            {tenant_proposal.toLocaleString(locale)}
                          </StyledSemiBoldSubtitle>
                        )}
                      </StyledSubContainer>
                      <StyledSubContainer>
                        <StyledTextBody>{t('expensesText')}</StyledTextBody>
                        <StyledTextBody>{`${
                          currency === 'EUR' ? '€' : currency
                        } ${fixed_costs}`}</StyledTextBody>
                      </StyledSubContainer>
                      {prop?.tari && prop.tari > 0 ? (
                        <StyledSubContainer>
                          <StyledTextBody>Tari</StyledTextBody>
                          <StyledTextBody>{`${
                            currency === 'EUR' ? '€' : currency
                          } ${prop.tari}`}</StyledTextBody>
                        </StyledSubContainer>
                      ) : null}
                    </StyledPriceSubContainer>
                  </StyledPriceContainer>
                  <ThankYouAccordion
                    paymentType={payment_type}
                    creditCardLastFourNumbers={card?.last4}
                    currency={currency === 'EUR' ? '€' : currency}
                    endDate={end_date}
                    locale={locale === 'en' ? 'en-uk' : locale!}
                    otherInfo={tenant_comment}
                    startDate={start_date}
                    tenantAge={tenant_age}
                    tenantGender={tenant_gender}
                    tenantReason={tenant_reason}
                    tenantPhoneNumber={tenant?.phone_no}
                  />
                </StyledSummaryContainer>
                {propertyOwner && !propertyOwner.agency && (
                  <BookingReceipt booking_id={id} />
                )}
              </StyledSection>
            </StyledSectionContainer>
            <Media
              queries={{
                mobile: `(max-width: calc(${theme.breakpoints.m} - 1px))`,
                desktop: `(min-width: ${theme.breakpoints.m})`,
              }}
              defaultMatches={{ mobile: true }}
            >
              {matches =>
                matches.mobile && (
                  <StyledStickyBox>{continueButton}</StyledStickyBox>
                )
              }
            </Media>
          </StyledGrid>
        )}
        {monthlyTotal && bookingId && (
          <iframe
            src={`https://nestpick.go2cloud.org/aff_l?offer_id=103&adv_sub=${bookingId}&amount=${monthlyTotal}`}
            width="1"
            height="1"
          />
        )}
      </>
    );
  }

  if (bookingData && !isObject(bookingData)) {
    return <StyledErrorMessage>{t('yourRequestFailed')}</StyledErrorMessage>;
  }

  return null;
};

export default ThankYou;
